import { PageHeader, Stat, Stats } from '../../components/office';
import { useState, useEffect, useMemo } from 'react';
import { staffApi, shiftsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@verlo/shared/components/ui/dropdown-menu';
import { toast } from 'sonner';
import Person from '../../components/people/Person';
import WeekPicker from '../../components/people/WeekPicker';
import { daysOf, mondayOf, throughWeek, ymd } from '../../lib/weeks';
import { cellStatus, kitchenRole, problems, soFar } from '../../lib/timesheet';
import { TimeField } from '@verlo/shared/components/ui/time-field';

// Timesheets (Back office sweep, Oct 2026: D2, mock "Timesheets"). Clocking happens on the
// till; here a manager checks and corrects. The cards compare like with like (what should
// have been worked by now against what was), each shift says quietly how it went, and
// Adjust times corrects one, with a reason that's kept.

const hoursWords = (h) => `${(h || 0).toFixed(1)}h`;

const TimesheetsPage = () => {
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [firstRota, setFirstRota] = useState(null);
  const [adjusting, setAdjusting] = useState(null);   // the shift whose times are being corrected
  const [form, setForm] = useState({ start: '', end: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const days = useMemo(() => daysOf(weekStart), [weekStart]);
  const now = new Date();
  const today = ymd(now);

  useEffect(() => {
    shiftsApi.firstDate().then((r) => r.data?.date && setFirstRota(new Date(`${r.data.date}T00:00:00`))).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, shiftsRes] = await Promise.all([staffApi.getAll(true), shiftsApi.getAll(ymd(days[0]), ymd(days[6]))]);
      setStaff(staffRes.data);
      setShifts(shiftsRes.data);
    } catch {
      toast.error("Couldn't load the timesheets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const byStaff = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const sums = useMemo(() => soFar(shifts, now), [shifts]); // eslint-disable-line react-hooks/exhaustive-deps
  const onNow = shifts.filter((s) => s.status === 'in_progress' && s.date === today);
  const kitchenNow = onNow.filter((s) => kitchenRole(s.role || byStaff[s.staff_id]?.role)).length;
  const weekScheduled = shifts.reduce((sum, s) => sum + (s.hours || 0), 0);
  const through = throughWeek(weekStart, now);
  const checks = problems(shifts, now, byStaff);

  const openAdjust = (shift) => {
    setAdjusting(shift);
    setForm({ start: shift.actual_start || shift.start_time, end: shift.actual_end || (shift.status === 'in_progress' ? '' : shift.end_time), reason: '' });
  };
  const saveAdjust = async () => {
    if (form.reason.trim().length < 3) { toast.error('Say why, so it’s on record'); return; }
    setSaving(true);
    try {
      await shiftsApi.adjust(adjusting.id, { start: form.start, end: form.end || null, reason: form.reason.trim() });
      toast.success(`${adjusting.staff_name}'s times corrected`);
      setAdjusting(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't correct the times");
    } finally { setSaving(false); }
  };

  const download = async (kind) => {
    try {
      const API_URL = import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('verlo_token');
      const res = await fetch(`${API_URL}/api/timesheets/export/${kind}?start_date=${ymd(days[0])}&end_date=${ymd(days[6])}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('export');
      if (kind === 'csv') {
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = `timesheet_${ymd(days[0])}_to_${ymd(days[6])}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        const html = await res.text();
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
        document.body.appendChild(iframe);
        iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } finally { setTimeout(() => iframe.remove(), 1500); } };
        iframe.srcdoc = html;
      }
    } catch {
      toast.error("Couldn't export the timesheet");
    }
  };

  const variance = sums.worked - sums.scheduled;
  const pct = sums.scheduled ? Math.abs(variance / sums.scheduled) * 100 : 0;

  return (
    <div className="space-y-5" data-testid="timesheets-page">
      <PageHeader
        title="Timesheets"
        purpose="Hours worked against the rota. Clocking happens on the till; here you check and correct."
        actions={(
          <>
            <WeekPicker weekStart={weekStart} onChange={setWeekStart} earliest={firstRota} testId="timesheet-week" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" data-testid="export-dropdown-btn">Export</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => download('csv')} data-testid="export-csv-btn">A spreadsheet (CSV)</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => download('pdf')} data-testid="export-pdf-btn">A printout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      />

      {loading ? <Skeleton className="h-[520px]" /> : (
        <>
          <Stats>
            <Stat label="Scheduled so far" value={hoursWords(sums.scheduled)} hint="Shifts that should have started by now" testId="ts-scheduled" />
            <Stat label="Worked so far" value={hoursWords(sums.worked)} hint="From the till's clock" testId="ts-worked" />
            <Stat label="Variance so far" value={`${variance > 0 ? '+' : variance < 0 ? '−' : ''}${Math.abs(variance).toFixed(1)}h`}
              hint={sums.scheduled ? `${pct.toFixed(1)}% ${variance < 0 ? 'under' : 'over'} the rota` : 'Nothing due yet'} testId="ts-variance" />
            <Stat label="On shift now" value={onNow.length}
              hint={onNow.length ? `${onNow.length - kitchenNow} front, ${kitchenNow} kitchen` : 'Nobody clocked in'} testId="ts-on-now" />
          </Stats>

          <div className="flex items-center gap-3" data-testid="ts-week-bar">
            <div className="h-1.5 w-48 overflow-hidden rounded-full bg-hairline">
              <div className="h-full bg-verlo" style={{ width: `${Math.round(through * 100)}%` }} />
            </div>
            <span className="text-[12.5px] text-ink-soft tabular-nums">
              {through <= 0 ? 'Not started yet' : through >= 1 ? 'The whole week' : `${now.toLocaleDateString('en-GB', { weekday: 'long' })}, ${Math.round(through * 100)}% through the week`}
              {' · '}of {hoursWords(weekScheduled)} scheduled this week
            </span>
          </div>

          <div className="max-h-[calc(100vh-330px)] overflow-auto border border-hairline bg-white" data-testid="ts-grid">
            <table className="w-full min-w-[1020px] border-separate border-spacing-0 text-[13px]">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-30 w-[220px] border-b border-r border-hairline bg-surface-2 px-3 py-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Person</th>
                  {days.map((d) => (
                    <th key={ymd(d)} className={`sticky top-0 z-20 border-b border-r border-hairline px-2 py-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] ${ymd(d) === today ? 'bg-verlo-tint text-verlo' : 'bg-surface-2 text-ink-faint'}`}>
                      {d.toLocaleDateString('en-GB', { weekday: 'short' })} {d.getDate()}
                    </th>
                  ))}
                  <th className="sticky top-0 z-20 w-[110px] border-b border-hairline bg-surface-2 px-3 py-2 text-right font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Week</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => {
                  const mine = shifts.filter((s) => s.staff_id === member.id);
                  if (!mine.length) return null;
                  const worked = mine.reduce((sum, s) => sum + (s.actual_hours || 0), 0);
                  const planned = mine.reduce((sum, s) => sum + (s.hours || 0), 0);
                  return (
                    <tr key={member.id} className="group" data-testid={`ts-row-${member.id}`}>
                      <td className="sticky left-0 z-10 border-b border-r border-hairline bg-white px-3 py-2 group-hover:bg-hover-tint">
                        <Person name={member.name} role={member.role} size="sm" />
                      </td>
                      {days.map((d) => {
                        const dayShifts = mine.filter((s) => s.date === ymd(d));
                        return (
                          <td key={ymd(d)} className={`border-b border-r border-hairline px-2 py-1.5 align-top ${ymd(d) === today ? 'bg-verlo-tint/40' : ''}`}>
                            {dayShifts.map((s) => {
                              const st = cellStatus(s, now);
                              const canAdjust = s.date <= today;
                              return (
                                <button key={s.id} type="button" disabled={!canAdjust} onClick={() => openAdjust(s)}
                                  className={`block w-full rounded px-1 py-0.5 text-left ${canAdjust ? 'hover:bg-white' : 'cursor-default'}`}
                                  title={s.adjusted ? `Adjusted by ${s.adjusted.by_name}: ${s.adjusted.reason}` : canAdjust ? 'Adjust times' : undefined}
                                  data-testid={`ts-shift-${s.id}`}>
                                  <span className={`block whitespace-nowrap font-mono text-[12.5px] tabular-nums ${st.future ? 'text-ink-faint' : 'text-ink'}`}>{s.start_time}–{s.end_time}</span>
                                  {st.words && <span className={`block whitespace-nowrap text-[11px] ${st.tone === 'warn' ? 'text-signal-warn' : st.tone === 'live' ? 'text-verlo' : 'text-ink-faint'}`}>{st.words}</span>}
                                </button>
                              );
                            })}
                            {!dayShifts.length && <span className="block px-1 text-ink-faint">—</span>}
                          </td>
                        );
                      })}
                      <td className="border-b border-hairline px-3 py-2 text-right tabular-nums">
                        <span className="font-mono text-ink">{hoursWords(worked)}</span> <span className="text-[11px] text-ink-faint">of {Math.round(planned)}h</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!shifts.length && <p className="px-4 py-10 text-center text-ink-soft">Nobody is on the rota this week.</p>}
          </div>

          {checks.slice(0, 3).map((c) => (
            <Notice key={c.shift.id} tone="warn" word="Check" data-testid="ts-check"
              action={<Button variant="outline" size="sm" onClick={() => openAdjust(c.shift)}>Adjust times</Button>}>
              <b className="font-semibold">{c.title}</b> {c.body}
            </Notice>
          ))}
          {checks.length > 3 && <p className="text-[12.5px] text-ink-soft">And {checks.length - 3} more to check: they're marked in amber above.</p>}
        </>
      )}

      <Dialog open={!!adjusting} onOpenChange={(o) => { if (!o) setAdjusting(null); }}>
        <DialogContent className="max-w-md" data-testid="ts-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust {adjusting?.staff_name}'s times</DialogTitle>
            <DialogDescription>
              {adjusting && new Date(`${adjusting.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}, rota {adjusting?.start_time}–{adjusting?.end_time}.
              {' '}What the till recorded stays on record; this says what it should have been.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5"><Label>Started</Label><TimeField value={form.start} onChange={(t) => setForm({ ...form, start: t })} testId="ts-adjust-start" className="w-full" /></div>
            <div className="space-y-1.5"><Label>Finished</Label><TimeField allowEmpty value={form.end} onChange={(t) => setForm({ ...form, end: t })} testId="ts-adjust-end" className="w-full" /></div>
            <p className="col-span-2 text-[12px] text-ink-faint">A finish earlier than the start is the next morning. Leave it empty if they're still on.</p>
            <div className="col-span-2 space-y-1.5">
              <Label>Why</Label>
              <Input value={form.reason} maxLength={200} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Forgot to clock out; left at 23:00" data-testid="ts-adjust-reason" />
            </div>
            {adjusting?.adjusted && (
              <p className="col-span-2 text-[12.5px] text-ink-soft">Last adjusted by {adjusting.adjusted.by_name}: “{adjusting.adjusted.reason}”</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjusting(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={saveAdjust} disabled={saving || !form.start} data-testid="ts-adjust-save">{saving ? 'Saving…' : 'Save the times'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetsPage;
